import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Users, Network, TrendingUp, Shield, ChevronRight, ArrowRight, BarChart3, CheckCircle } from 'lucide-react';

const Landing = () => {
  return (
    <div className="min-h-screen bg-neutral-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-neutral-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary-900 flex items-center justify-center">
              <span className="text-white font-serif text-xl font-semibold">M</span>
            </div>
            <span className="font-serif text-2xl font-medium text-primary-900">Medichi Tribe</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button 
                variant="ghost" 
                className="text-primary-900 hover:bg-primary-50"
                data-testid="nav-login-btn"
              >
                Login
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                className="bg-primary-900 text-white hover:bg-primary-800 rounded-full px-6"
                data-testid="nav-register-btn"
              >
                Join the Tribe
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1615634364452-8daf3851f2f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwZXh0ZXJpb3J8ZW58MHx8fHwxNzY4MjY0MDkwfDA&ixlib=rb-4.1.0&q=85"
            alt="Luxury Real Estate"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/40" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in">
            <span className="inline-block px-4 py-2 rounded-full bg-primary-400/20 text-primary-200 text-sm font-medium mb-6">
              Real Estate Recruitment & Income Network
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
              Build Your Real Estate Team. <span className="text-sky-400">Multiply Your Income.</span>
            </h1>
            <p className="text-lg text-primary-200 leading-relaxed mb-8 max-w-lg">
              Medichi Tribe is a recruit-and-earn system that allows you to grow your income through direct sales and indirect commissions by building a strong network of real estate marketers.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 h-14 text-lg font-medium shadow-lg shadow-sky-500/30 transition-all hover:scale-105"
                  data-testid="hero-join-btn"
                >
                  Activate Your Recruitment Link
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 h-14 text-lg"
                  data-testid="hero-login-btn"
                >
                  Member Login
                </Button>
              </Link>
            </div>
          </div>
          
          <div className="hidden lg:block animate-fade-in-delay-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-sky-500/20 rounded-3xl blur-xl" />
              <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20">
                <p className="text-primary-200 text-center text-sm mb-6">
                  A growing network built on real sales, real people, and real commissions.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4">
                    <p className="text-4xl font-bold text-white mb-2">500+</p>
                    <p className="text-primary-200 text-sm">Active Marketers</p>
                  </div>
                  <div className="text-center p-4">
                    <p className="text-4xl font-bold text-sky-400 mb-2">$2M+</p>
                    <p className="text-primary-200 text-sm">Total Sales</p>
                  </div>
                  <div className="text-center p-4">
                    <p className="text-4xl font-bold text-white mb-2">150+</p>
                    <p className="text-primary-200 text-sm">Properties Listed</p>
                  </div>
                  <div className="text-center p-4">
                    <p className="text-4xl font-bold text-sky-400 mb-2">3%</p>
                    <p className="text-primary-200 text-sm">Indirect Commission</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-serif text-4xl md:text-5xl text-primary-900 mb-4">Why Join Medichi Tribe?</h2>
            <p className="text-neutral-text-muted text-lg max-w-2xl mx-auto">
              Medichi Tribe is designed to help you earn more by combining personal selling with team-based income leverage.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Users,
                title: 'Recruit & Grow',
                description: 'Build your real estate team using your unique referral link and earn when the people you recruit succeed.',
              },
              {
                icon: TrendingUp,
                title: 'Earn Indirect Commissions',
                description: 'Receive 3% indirect commission every time a marketer you personally recruit makes a sale — in addition to your direct earnings.',
              },
              {
                icon: BarChart3,
                title: 'Track Your Downline',
                description: 'Monitor your recruits, commissions, and performance in real time from your personalized dashboard.',
              },
              {
                icon: Shield,
                title: 'Secure & Transparent',
                description: 'All registrations, sales, and commissions are tracked securely with clear records and full visibility.',
              },
            ].map((feature, index) => (
              <div
                key={feature.title}
                className="group p-8 rounded-2xl bg-neutral-background hover:bg-white hover:shadow-floating transition-all duration-300"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 rounded-xl bg-primary-100 flex items-center justify-center mb-6 group-hover:bg-primary-900 transition-colors">
                  <feature.icon className="h-7 w-7 text-primary-900 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-semibold text-primary-900 mb-3">{feature.title}</h3>
                <p className="text-neutral-text-muted leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-primary-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl text-primary-900 mb-6">
                How Medichi Tribe Helps You Earn More
              </h2>
              <p className="text-neutral-text-muted text-lg mb-8">
                Most marketers earn only from what they sell personally.
              </p>
              <p className="text-primary-900 font-medium text-lg mb-6">
                Medichi Tribe allows you to:
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  'Sell directly and earn commissions',
                  'Recruit others and earn when they sell',
                  'Build a team that increases your earning potential over time',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-primary-800">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-primary-700 font-medium italic">
                This is how income scales — through leverage, not burnout.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-primary-200 rounded-3xl blur-2xl opacity-50" />
              <img
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxyZWFsJTIwZXN0YXRlJTIwdGVhbXxlbnwwfHx8fDE3MTY5MjM0NTB8MA&ixlib=rb-4.1.0&q=85"
                alt="Real Estate Team"
                className="relative rounded-2xl shadow-floating w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who Can Join Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-primary-900 mb-4">Who Can Join Medichi Tribe?</h2>
            <p className="text-neutral-text-muted text-lg max-w-2xl mx-auto">
              Anyone can join Medichi Tribe.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <p className="text-center text-primary-900 font-medium text-lg mb-10">It's ideal for:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Realtors and real estate marketers',
                'Salespeople and closers',
                'Networkers and community builders',
                'Anyone who wants to multiply income beyond personal effort',
              ].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-4 p-6 rounded-xl bg-primary-50 border border-primary-100"
                >
                  <div className="w-10 h-10 rounded-full bg-primary-900 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-primary-800 font-medium">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-neutral-text-muted mt-10 text-lg">
              No prior experience required — just the ability to connect people to opportunity.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-primary-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_#38BDF8_0%,_transparent_50%)]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">
            Ready to Multiply Your Income Through Real Estate?
          </h2>
          <p className="text-primary-200 text-lg mb-10 max-w-2xl mx-auto">
            Activate your recruitment link and start earning from both your sales and your team's success.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-12 h-14 text-lg font-medium shadow-lg shadow-sky-500/30"
              data-testid="cta-join-btn"
            >
              Activate Your Recruitment Link
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary-950 border-t border-primary-800 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary-800 flex items-center justify-center">
                <span className="text-white font-serif text-lg font-semibold">M</span>
              </div>
              <span className="font-serif text-xl text-white">Medichi Tribe</span>
            </div>
            <p className="text-primary-400 text-sm">
              © 2025 Medichi Tribe. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
